import { html, PropertyValueMap, TemplateResult, CSSResultGroup } from 'lit';
import { property, state } from 'lit/decorators.js';
import { BlocksElement } from '../BlocksElement';
import { BlocksSideMenuItem } from '../side-menu-item/side-menu-item';
import styles from './side-menu.styles';

/**
 * @slot default - The component content
 * @slot header - The header content
 * @slot footer - The header content
 * @csspart base - The component's base wrapper.
 * @csspart header-wrap - The component's header wrapper.
 * @csspart part-header - The component's header slot.
 * @csspart nav - The component's nav element.
 * @csspart list - The component's list.
 * @csspart part-default - The component's default slot.
 * @csspart footer-wrap - The component's footer wrapper.
 * @csspart part-footer - The component's footer slot.
 */
export class BlocksSideMenu extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * is Active
   * 1) Panel is open when set to true. Close when set to false
   */
  @property({ type: Boolean })
  isCollapsed: boolean | undefined;

  /**
   * Breakpoint below which side-menu switches to mobile view. Defaults to 768px.
   */
  @property({ type: Number })
  mobileBreakPoint = 768;

  @state()
  private isMobile = false;

  private appBodyOverflow!: string;

  /**
   * Query the sideNavItems from light dom
   */
  private get sideMenuItems(): BlocksSideMenuItem[] {
    return Array.from(this.querySelectorAll('cat-side-menu-item'));
  }

  /**
   * Query the Body
   */

  private get queryBody() {
    return document.querySelector<HTMLBodyElement>('body')!;
  }

  /**
   * Connected callback lifecycle
   * 1) Add window resize event listener
   */
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.onResize); /* 1 */
    const body = this.queryBody;
    this.appBodyOverflow = body.style.overflow;
  }

  /**
   * Disconnected callback lifecycle
   * 1) Remove window resize event listener
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.onResize); /* 1 */
  }

  /**
   * Resize Handler
   */
  private onResize = () => {
    const body = this.queryBody;
    if (window.innerWidth < this.mobileBreakPoint) {
      this.isMobile = true;
      if (this.isCollapsed === false) {
        body.style.overflow = 'hidden';
      } else {
        body.style.overflow = this.appBodyOverflow ? this.appBodyOverflow : '';
      }
    } else {
      this.isMobile = false;
      body.style.overflow = this.appBodyOverflow ? this.appBodyOverflow : '';
    }
    this.sideMenuItems.forEach((sideMenuItem: BlocksSideMenuItem) => {
      sideMenuItem.isMobile = this.isMobile;
    });
  };

  /**
   * First updated lifecycle when component is first updated on page
   */
  protected firstUpdated(): void {
    this.onResize();
  }
  /**
   * When component is updated
   * 1) If the side menu is collapsed, turn the `isCollapsed` property on on the items
   * 2) Otherwise, turn `isCollapsed` off
   * 3) If mobile is detected, body overflow should be hidden on expanded mode.
   */
  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('isCollapsed')) {
      this.onResize(); /* 3 */
      const sideMenuItems = this.querySelectorAll('cat-side-menu-item');
      sideMenuItems.forEach((element: BlocksSideMenuItem) => {
        if (this.isCollapsed === true) {
          element.isCollapsed = true; /* 1 */
          if (element.isActive === true) element.isActive = false;
        } else {
          element.isCollapsed = false; /* 2 */
        }
      });
    }
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-side-menu', {
      'cat-is-collapsed': this.isCollapsed === true,
      'cat-is-mobile': this.isMobile === true
    });

    return html`
      <div part="base" class="${componentClassNames}">
        ${this.slotNotEmpty('header') &&
        html`
          <header part="header-wrap" class="cat-c-side-menu__header">
            <slot part="part-header" name="header"></slot>
          </header>
        `}
        <nav part="nav" class="cat-c-side-menu__nav" aria-label="Side menu nav">
          <ul part="list" role="list" class="cat-c-side-menu__list">
            <slot part="part-default"></slot>
          </ul>
        </nav>
        ${this.slotNotEmpty('footer') &&
        html`
          <footer part="footer-wrap" class="cat-c-side-menu__footer">
            <slot part="part-footer" name="footer"></slot>
          </footer>
        `}
      </div>
    `;
  }
}

if (customElements.get('cat-side-menu') === undefined) {
  customElements.define('cat-side-menu', BlocksSideMenu);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-side-menu': BlocksSideMenu;
  }
}
